import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { IS_SPINNER_ACTIVE } from '../../../../../util/interceptors';
import { UrlService } from '../../../../../../environments/urlService';
import { NOTIFICATIONS, NotificationsService } from '../../../../../core/service/notifications.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private readonly url;
  private readyForChange = new BehaviorSubject<boolean>(true);

  constructor(private httpClient: HttpClient,
    private urlService: UrlService,
    private notificationsService: NotificationsService) {
    this.url = this.urlService.getFmsUrl() + 'image';
  }

  setReadyForChange(b: boolean) {
    this.readyForChange.next(b);
  }

  get readyForChangeGetter() {
    return this.readyForChange;
  }

  /**
   * Uploads a file to a s3.
   *
   * This method prepares an image metadata object (imageCreatorDTO) based on the
   * given parameters and sends a POST request to the server to obtain a presigned URL.
   * After obtaining the presigned URL, the method performs an XMLHttpRequest to
   * upload the file to the server.
   *
   * @param file - The file object representing the image to upload.
   * @param imageName - The name that will be modified to the pattern UUID.extension
   * @param folder - The folder where the image will be stored.
   * @return new image name
   */
  public async uploadFile(file, imageName, folder): Promise<string> {
    const imageCreatorDTO = {
      imageName,
      folderName: folder,
      fileType: file.type,
    };
    let generatedLinkAndNewFileNameRs: GeneratedLinkAndNewFileNameRs = null;
    this.setReadyForChange(false);
    try {
      generatedLinkAndNewFileNameRs = await this.getGeneratedLink(imageCreatorDTO);
    } catch (e) {
      this.setReadyForChange(true);
      this.notificationsService.error(NOTIFICATIONS.URL_FOR_UPLOAD);
    }
    const newFileName = await this.uploadToCloud(file, generatedLinkAndNewFileNameRs);
    this.setReadyForChange(true);
    return newFileName;
  }

  private async getGeneratedLink(imageCreatorDTO): Promise<GeneratedLinkAndNewFileNameRs> {
    return this.httpClient.post<GeneratedLinkAndNewFileNameRs>(
      this.url + '/generate-url', imageCreatorDTO,
      {
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
        responseType: 'json',
      }).toPromise();
  }

  private async uploadToCloud(file, generatedLinkAndNewFileNameRs: GeneratedLinkAndNewFileNameRs): Promise<string> {
    const newFile = new File([file], generatedLinkAndNewFileNameRs.newName, { type: file.type });
    return new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', generatedLinkAndNewFileNameRs.presignedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.setRequestHeader('x-amz-acl', 'public-read');
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve(generatedLinkAndNewFileNameRs.newName);
          } else {
            this.setReadyForChange(true);
            reject(this.notificationsService.error(NOTIFICATIONS.CLOUD_UPLOAD));
          }
        }
      };
      xhr.send(newFile);
    });

  }
}

interface GeneratedLinkAndNewFileNameRs {
  presignedUrl: string;
  newName: string;
}
